import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { BackgroundValueType } from '@elfsight/configurator-contracts';
import { LOCAL_WIDGET_ID } from '@elfsight/lib-utils';
import {
  FormProvider,
  WidgetProvider
} from '@elfsight/widget-form-builder/widget';
import type { PopupProps } from '@elfsight/widget-modal';
import { Popup } from '@elfsight/widget-modal';
import { getGradientValue, WidgetBackground } from '@elfsight/widget-ui';
import {
  COLOR_BLACK,
  COLOR_WHITE
} from '@elfsight/widget-ui/src/button/constants';
import {
  getColorWithAlpha,
  getInvertedColor,
  isTransparentColor,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useCalculations } from '../../calculations-context';
import { useCalculatorConditionalLogic } from '../../conditional-logic-context';
import { renderThumbnailModeMapping } from '../../render-thumbnail-mode-mapping';
import { useEmailSubmitPopup } from '../../use-email-submit-popup';
import { useFieldsScope } from '../../use-fields-scope';
import { Form } from './form/form';
import {
  prepareCalculationsFields,
  prepareCalculatorFields,
  prepareSubmitFormFields
} from './utils';

const getContentPadding = (
  innerPaddingVertical: number,
  innerPaddingHorizontal: number,
  hasScroll: boolean
) => {
  const paddingRight = hasScroll
    ? innerPaddingHorizontal - 10
    : innerPaddingHorizontal;

  return {
    paddingTop: innerPaddingVertical,
    paddingRight,
    paddingBottom: innerPaddingVertical,
    paddingLeft: innerPaddingHorizontal
  };
};

const getBackgroundColor = (
  initialColor: string | undefined,
  textColor: string
) => {
  if (isTransparentColor(initialColor)) {
    return getInvertedColor(textColor);
  }

  return initialColor ?? COLOR_WHITE;
};

export const EmailSubmitPopup = ({ show, onCloseRequest }: PopupProps) => {
  const settings = useSettings();
  const {
    textColor,
    font,
    calculatorEmailSubmitFields,
    fields: settingsFields,
    calculations: settingsCalculations,
    thousandsSeparator,
    decimalSeparator,
    widgetId
  } = settings;
  const { background } = renderThumbnailModeMapping(settings);
  const isGradientBackground = background.type === 'gradient';
  const derivedBackground = isGradientBackground
    ? {
        ...background,
        type: 'color' as BackgroundValueType,
        color: 'transparent'
      }
    : background;
  const [hasScroll, setHasScroll] = useState(false);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const { displayedCalculations, displayedFields: displayedCalculatorFields } =
    useCalculatorConditionalLogic();
  const calculations = useCalculations();
  const fieldsScope = useFieldsScope();

  const {
    isFormPopupOpen,
    popupStyles: {
      outerMargin,
      innerPaddingHorizontal,
      innerPaddingVertical,
      fullScreen,
      borderRadius
    }
  } = useEmailSubmitPopup();

  const contentPadding = getContentPadding(
    innerPaddingVertical,
    innerPaddingHorizontal,
    hasScroll
  );

  const checkScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollableRef.current) {
        const hasVerticalScroll =
          scrollableRef.current.scrollHeight >
          scrollableRef.current.clientHeight;

        setHasScroll(hasVerticalScroll);
      }
    });
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [isFormPopupOpen, checkScroll]);

  const preparedFields = useMemo(() => {
    const preparedSubmitFormFields = prepareSubmitFormFields(
      calculatorEmailSubmitFields
    );

    const preparedCalculatorFormFields = prepareCalculatorFields(
      settingsFields,
      displayedCalculatorFields,
      fieldsScope,
      thousandsSeparator,
      decimalSeparator
    );

    const preparedCalculationsFormFields = prepareCalculationsFields(
      settingsCalculations,
      displayedCalculations,
      calculations,
      thousandsSeparator,
      decimalSeparator
    );

    return [
      ...preparedSubmitFormFields,
      ...preparedCalculatorFormFields,
      ...preparedCalculationsFormFields
    ];
  }, [
    calculatorEmailSubmitFields,
    settingsFields,
    displayedCalculatorFields,
    fieldsScope,
    thousandsSeparator,
    decimalSeparator,
    settingsCalculations,
    displayedCalculations,
    calculations
  ]);

  return (
    <WidgetProvider>
      <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
        <StyledPopup
          show={show}
          ariaLabel="Email Submit Popup"
          onCloseRequest={onCloseRequest}
          withBackdrop
          withCloseControl
          closeControlColor={getColorWithAlpha(COLOR_BLACK, 0.7)}
          closeOnEscape
          width={500}
          margin={outerMargin}
          closeOnOutsideClick
          disableScroll
          withoutTransition
          fullscreen={fullScreen}
          fixed
          style={{
            backgroundColor: getBackgroundColor(background.color, textColor),
            backgroundImage:
              background.type === 'gradient'
                ? getGradientValue(background.gradient)
                : undefined
          }}
          _borderRadius={borderRadius}
        >
          <ScrollableWrapper
            ref={scrollableRef}
            _textColor={textColor}
            _hasScroll={hasScroll}
          >
            <StyledWidgetBackground
              {...derivedBackground}
              contentStyle={{
                fontFamily: font?.value,
                // При использовании shorthand падает ошибка и паддинг не применяется
                ...contentPadding
              }}
              _outerMargin={outerMargin}
            >
              <Form fields={preparedFields} />
            </StyledWidgetBackground>
          </ScrollableWrapper>
        </StyledPopup>
      </FormProvider>
    </WidgetProvider>
  );
};

const StyledPopup = styled(Popup)<{ _borderRadius: number }>`
  position: absolute;
  overflow: hidden;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
`;

const ScrollableWrapper = styled.div<{
  _textColor: string;
  _hasScroll: boolean;
}>`
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  width: ${({ _hasScroll }) => (_hasScroll ? 'calc(100% - 4px)' : '100%')};

  @supports not selector(-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.1)}
      transparent;
  }

  ${({ _textColor }) =>
    scrollbarColoredMixin(getColorWithAlpha(_textColor, 0.1))};

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.3)};
  }
`;

const StyledWidgetBackground = styled(WidgetBackground)<{
  _outerMargin: number;
}>`
  ${({ _outerMargin }) => `max-height: calc(100vh - ${_outerMargin * 2}px)`};
`;
